import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getTopics } from '../i18n/topics';
import { sectionPath, homePath } from '../siteRoutes';
import { Phone, Mail, MapPin, Facebook, Instagram, Music2, ShieldCheck } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../i18n';
import { TrustNote, TrustLegal, TRUST_LABEL } from './Trust';

const Footer = () => {
  const { hotelInfo } = useHotel();
  const { lang } = useLanguage();
  const t = (key) => getTranslation(lang, key);

  const { pathname } = useLocation();

  return (
    <footer className="bg-gradient-to-b from-[#1a1f2e] to-[#0f1318] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Hotel Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/logo.png"
                alt="Hotel La Rosa Negra"
                className="w-12 h-12 rounded-lg shadow-lg object-cover"
                data-testid="footer-logo"
              />
              <div>
                <h3 className="text-xl font-bold">Hotel La Rosa Negra</h3>
                <div className="flex items-center gap-1 text-orange-400 text-sm">
                  {[...Array(hotelInfo.stars || 3)].map((_, i) => (
                    <span key={`star-${i}`}>★</span>
                  ))}
                  <span className="text-gray-400 ml-1">{hotelInfo.stars}-{t('footer.starHotel')}</span>
                </div>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">{t('footer.description')}</p>
            <div className="flex items-center gap-3">
              <a href={hotelInfo.social?.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all" aria-label="Facebook">
                <Facebook size={18} />
              </a>
              <a href={hotelInfo.social?.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all" aria-label="Instagram">
                <Instagram size={18} />
              </a>
              <a href={hotelInfo.social?.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all" aria-label="TikTok">
                <Music2 size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to={sectionPath('rooms', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-rooms">{t('footer.rooms')}</Link>
              </li>
              <li>
                <Link to={sectionPath('amenities', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-amenities">{t('footer.amenities')}</Link>
              </li>
              <li>
                <Link to={sectionPath('gallery', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-gallery">{t('footer.gallery')}</Link>
              </li>
              <li>
                <Link to={sectionPath('virtual-tour', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-virtual-tour">{t('nav.virtualTour')}</Link>
              </li>
              <li>
                <Link to={sectionPath('location', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-location">{t('footer.location')}</Link>
              </li>
              <li>
                <Link to={sectionPath('reviews', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-reviews">{t('footer.reviews')}</Link>
              </li>
              <li>
                <Link to={sectionPath('faq', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-faq">{t('faq.subtitle')}</Link>
              </li>
              <li>
                <Link to={sectionPath('contact', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-contact">{t('nav.contact')}</Link>
              </li>
              <li>
                <Link to={sectionPath('ulcinj-guide', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid="footer-link-ulcinj-guide">{t('guide.nav')}</Link>
              </li>
              <li>
                <Link to={sectionPath('trust', pathname)} className="text-gray-400 hover:text-orange-400 transition-colors text-sm inline-flex items-center gap-1" data-testid="footer-link-trust"><ShieldCheck className="w-3.5 h-3.5" /> {TRUST_LABEL[lang] || TRUST_LABEL.en}</Link>
              </li>
              {getTopics(lang).topics.map((x) => (
                <li key={x.slug}>
                  <Link to={`${homePath(pathname) === '/' ? '' : homePath(pathname)}/stay/${x.slug}`} className="text-gray-400 hover:text-orange-400 transition-colors text-sm" data-testid={`footer-topic-${x.slug}`}>{x.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li>
                <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors text-sm">{t('footer.privacy')}</a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors text-sm">{t('footer.terms')}</a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors text-sm">{t('footer.cookies')}</a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors text-sm">{t('footer.cancellation')}</a>
              </li>
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t('footer.contactInfo')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin size={18} className="flex-shrink-0 mt-0.5 text-orange-400" />
                <span>{hotelInfo.location}</span>
              </li>
              <li>
                <a href={`tel:${hotelInfo.phone}`} className="flex items-center gap-2 text-gray-400 hover:text-orange-400 transition-colors text-sm">
                  <Phone size={18} className="flex-shrink-0 text-orange-400" />
                  <span>{hotelInfo.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${hotelInfo.email}`} className="flex items-center gap-2 text-gray-400 hover:text-orange-400 transition-colors text-sm break-all">
                  <Mail size={18} className="flex-shrink-0 text-orange-400" />
                  <span>{hotelInfo.email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar — legal identity + how we source trust data */}
        <div className="border-t border-gray-700 mt-12 pt-8 text-center space-y-3">
          <TrustLegal />
          <TrustNote className="justify-center text-gray-500" />
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} La Rosa Negra. {t('footer.rights')}.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
